import { useEffect, type RefObject } from "react";

/** How long after opening to keep pinning, in ms. */
const SETTLE_MS = 600;

/**
 * Keeps a freshly opened container scrolled to the top.
 *
 * The embedded cmdk Commands scroll their selected item into view, and
 * `scrollIntoView` drags every scrollable ancestor along with it — so the
 * dialog can land scrolled partway down. One reset on open isn't enough: option
 * lists resolve asynchronously (a store that loads after mount), and cmdk
 * re-runs its scroll as they grow. So pin for a short settling window instead,
 * releasing early on the first sign of real user input so we never fight
 * someone who scrolls immediately.
 *
 * Both the container and its scrollable ancestor within `boundary` are reset —
 * a tall dialog makes the popover content itself scrollable too.
 */
export function usePinScrollToTop(
  ref: RefObject<HTMLElement | null>,
  active: boolean,
  boundary: string,
  /** Changing this restarts the pin — e.g. swapping the container's content. */
  restartKey?: string,
) {
  useEffect(() => {
    if (!active) return;

    const outer = ref.current?.closest<HTMLElement>(boundary);
    let frame = 0;
    let released = false;

    const release = () => {
      released = true;
      window.cancelAnimationFrame(frame);
    };

    const pin = () => {
      if (released) return;
      if (ref.current) ref.current.scrollTop = 0;
      if (outer) outer.scrollTop = 0;
      frame = window.requestAnimationFrame(pin);
    };

    pin();
    const timer = window.setTimeout(release, SETTLE_MS);
    const events = ["wheel", "touchmove", "keydown", "pointerdown"] as const;
    // Capture phase: the dialog's scroll container stops wheel/touch
    // propagation, so a bubbling listener here would never see them.
    for (const event of events) {
      window.addEventListener(event, release, { capture: true, passive: true });
    }

    return () => {
      release();
      window.clearTimeout(timer);
      for (const event of events) {
        window.removeEventListener(event, release, { capture: true });
      }
    };
  }, [ref, active, boundary, restartKey]);
}
